import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { formatTotals, addToTotals } from '../currencyOptions';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent
} from '@mui/material';

interface Payment {
  lotName: string;
  amount: number;
  currency?: string;
  receiptTS: any;
}

/** receiptTS is written as a Timestamp by some paths and a string by others. */
function toDate(value: any): Date | null {
  if (!value) return null;
  try {
    if (typeof value.toDate === 'function') return value.toDate();
    if (typeof value.seconds === 'number') return new Date(value.seconds * 1000);
    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? null : parsed;
  } catch {
    return null;
  }
}

interface VendorEarningsProps {
  vendorId: string;
}

export default function VendorEarnings({ vendorId }: VendorEarningsProps) {
  const [loading, setLoading] = useState(true);
  const [payments, setPayments] = useState<Payment[]>([]);

  useEffect(() => {
    const fetchEarnings = async () => {
      setLoading(true);
      try {
        // Query payments directly by vendorId (more efficient)
        const paymentsQuery = query(
          collection(db, 'payments'),
          where('vendorId', '==', vendorId)
        );
        const paymentsSnapshot = await getDocs(paymentsQuery);
        const vendorPayments = paymentsSnapshot.docs.map(doc => doc.data() as Payment);

        console.log('Vendor payments found:', vendorPayments.length);
        setPayments(vendorPayments);
      } catch (error) {
        console.error('Error fetching earnings:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchEarnings();
  }, [vendorId]);

  // Revenue is bucketed per currency. Lots are priced multi-currency, so
  // adding a CAD payment to a USD one and printing '$' in front would be
  // quietly wrong; a single-currency partner still sees a single figure.
  const totalsMatching = (predicate: (paidAt: Date | null) => boolean) => {
    const totals: Record<string, number> = {};
    payments.forEach((payment) => {
      if (predicate(toDate(payment.receiptTS))) {
        addToTotals(totals, payment.amount, payment.currency);
      }
    });
    return totals;
  };

  const totalEarnings = () => totalsMatching(() => true);

  const todayEarnings = () => {
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    return totalsMatching(
      (paidAt) => paidAt !== null && paidAt.getTime() >= startOfToday.getTime()
    );
  };

  const thisMonthEarnings = () => {
    const now = new Date();
    return totalsMatching(
      (paidAt) =>
        paidAt !== null &&
        paidAt.getMonth() === now.getMonth() &&
        paidAt.getFullYear() === now.getFullYear()
    );
  };

  const getEarningsByLot = () => {
    const byLot: Record<string, Record<string, number>> = {};

    payments.forEach((payment) => {
      const lotName = payment.lotName || 'Unknown';
      byLot[lotName] = byLot[lotName] || {};
      addToTotals(byLot[lotName], payment.amount, payment.currency);
    });

    return Object.entries(byLot)
      .map(([lotName, totals]) => ({
        lotName,
        totals,
        // Sort on the partner's primary currency bucket, falling back to the
        // largest bucket, so the ordering stays meaningful when mixed.
        sortKey: Math.max(0, ...Object.values(totals)),
      }))
      .sort((a, b) => b.sortKey - a.sortKey);
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Earnings
      </Typography>

      {/* Summary Cards */}
      <Box sx={{ display: 'flex', gap: 3, mb: 4, flexWrap: 'wrap' }}>
        <Box sx={{ flex: '1 1 300px', minWidth: 300 }}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Today's Revenue
              </Typography>
              <Typography variant="h4">
                {formatTotals(todayEarnings())}
              </Typography>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: '1 1 300px', minWidth: 300 }}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                This Month's Revenue
              </Typography>
              <Typography variant="h4">
                {formatTotals(thisMonthEarnings())}
              </Typography>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: '1 1 300px', minWidth: 300 }}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Revenue
              </Typography>
              <Typography variant="h4">
                {formatTotals(totalEarnings())}
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Earnings by Lot */}
      <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
        Revenue by Parking Lot
      </Typography>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Parking Lot</TableCell>
              <TableCell align="right">Total Revenue</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {getEarningsByLot().length > 0 ? (
              getEarningsByLot().map((lot, index) => (
                <TableRow key={index}>
                  <TableCell>{lot.lotName}</TableCell>
                  <TableCell align="right">{formatTotals(lot.totals)}</TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={2} align="center">
                  No earnings data available
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
